/**
 * Utility functions for generating breadcrumb links automatically
 */

/**
 * Map of route paths to their display names and optional parent routes
 * This serves as a configuration for the breadcrumb generator
 */
const routeConfig = {
  // Main sections
  "app/main": { label: "Home", parent: null, hide: true },
  "app/dashboard": { label: "Dashboard", parent: "app/main" },
  "app/accounting": { label: "Accounting", parent: "app/main" },
  "app/customers": { label: "Customers", parent: "app/main" },
  "app/vendors": { label: "Vendors", parent: "app/main" },
  "app/inventory": { label: "Inventory", parent: "app/main" },
  "app/reports": { label: "Reports", parent: "app/main" },
  "app/settings": { label: "Settings", parent: "app/main" },
  "app/clinic": { label: "Clinic Management", parent: "app/main" },
  "app/pos": { label: "Point of Sale", parent: "app/main" },
  "app/pos/legacy": { label: "Legacy POS", parent: "app/pos" },
  "app/pos/terminal/:terminalId": { label: "POS Terminal", parent: "app/pos" },
  "app/pos/receipt/:id": { label: "POS Receipt", parent: "app/pos" },
  "app/pos/:id": { label: "POS Receipt", parent: "app/pos" },
  "app/properties": { label: "Properties", parent: "app/main" },
  "app/rentals": { label: "Rentals", parent: "app/main" },
  "app/buildings": { label: "Buildings", parent: "app/main" },
  "app/purchases": { label: "Purchases", parent: "app/main" },
  "app/payrolls": { label: "Payrolls", parent: "app/main" },
  "app/assets": { label: "Assets", parent: "app/main" },
  "app/attendances": { label: "Attendance", parent: "app/attendance/home" },
  "app/messaging": { label: "Messaging", parent: "app/main" },
  "app/letters": { label: "Letters", parent: "app/main" },
  "app/notifications": { label: "Notifications", parent: "app/main" },
  "app/projects": { label: "Projects", parent: "app/main" },
  "app/vouchers": { label: "Vouchers", parent: "app/main" },
  "app/coupons": { label: "Coupons", parent: "app/main" },
  "app/todos": { label: "To-Do Lists", parent: "app/main" },
  "app/website": { label: "My Website", parent: "app/main" },
  "app/excel": { label: "Import / Export", parent: "app/main" },
  "app/excel-workbooks": { label: "Excel Workbooks", parent: "app/main" },
  "app/excel-workbooks/:id": { label: "Workbook Editor", parent: "app/excel-workbooks" },
  "app/workflow-settings": { label: "Workflow Settings", parent: "app/main" },
  "app/workflow-inbox": { label: "Approval Inbox", parent: "app/workflow-settings" },
  "app/my-subscription": { label: "My Subscription", parent: "app/settings" },
  "app/my-billing": { label: "My Billing", parent: "app/settings" },

  // Settings subsections
  "app/settings/general": { label: "General Settings", parent: "app/settings" },
  "app/settings/zatca": { label: "ZATCA Integration", parent: "app/settings" },
  "app/settings/tax-settings": { label: "Tax Settings", parent: "app/settings" },
  "app/settings/pos-settings": { label: "POS Settings", parent: "app/settings" },
  "app/settings/attendance": { label: "Attendance Settings", parent: "app/settings" },
  "app/settings/ai": { label: "AI Settings", parent: "app/settings" },
  "app/settings/mfa": { label: "MFA Settings", parent: "app/settings" },
  "app/settings/modules": { label: "Module Settings", parent: "app/settings" },
  "app/security-settings": { label: "Security Settings", parent: "app/settings" },
  "app/barcode-settings": { label: "Barcode Settings", parent: "app/settings" },
  "app/admin-settings": { label: "Admin Settings", parent: "app/settings" },
  "app/admin-settings/companies": { label: "Companies", parent: "app/admin-settings" },
  "app/admin-settings/packages": { label: "Product Packages", parent: "app/admin-settings" },
  "app/admin-settings/features": { label: "Features", parent: "app/admin-settings" },
  "app/accounting-settings": { label: "Accounting Settings", parent: "app/settings" },
  "app/sms-providers": { label: "SMS Providers", parent: "app/settings" },
  "app/communication-center": { label: "Communication Center", parent: "app/main" },
  "app/profile": { label: "Profile Settings", parent: "app/settings" },

  // Accounting subsections
  "app/journals": { label: "Journals", parent: "app/accounting" },
  "app/journals/reports/trial-balance": { label: "Trial Balance Report", parent: "app/journals" },
  "app/journals/reports/journal": { label: "Journal Report", parent: "app/journals" },
  "app/ledger": { label: "General Ledger", parent: "app/accounting" },
  "app/Accounts": { label: "Chart of Accounts", parent: "app/accounting" },
  "app/accounts": { label: "Chart of Accounts", parent: "app/accounting" },
  "app/transaction": { label: "Transaction", parent: "app/accounting" },
  "app/transactions": { label: "Transactions", parent: "app/accounting" },
  "app/transactions/:pageNumber": { label: "Transactions", parent: "app/accounting" },
  "app/transactions/client/:clientId": { label: "Client Transactions", parent: "app/transactions" },
  "app/transaction/edit/:id": { label: "Edit Transaction", parent: "app/transactions" },
  "app/trial-balance": { label: "Trial Balance", parent: "app/accounting" },
  "app/balance-sheet": { label: "Balance Sheet", parent: "app/accounting" },
  "app/income-statement": { label: "Income Statement", parent: "app/accounting" },
  "app/cash-flow": { label: "Cash Flow", parent: "app/accounting" },
  "app/comprehensive-income": { label: "Comprehensive Income", parent: "app/accounting" },
  "app/stockholders-equity": { label: "Stockholders Equity", parent: "app/accounting" },
  "app/general-ledger": { label: "General Ledger", parent: "app/accounting" },
  "app/aging-report": { label: "Aging Report", parent: "app/accounting" },
  "app/account-statement": { label: "Account Statement", parent: "app/accounting" },
  "app/vat-report": { label: "VAT Report", parent: "app/accounting" },
  "app/project-profitability": { label: "Project Profitability", parent: "app/accounting" },
  "app/accounting-reports": { label: "Accounting Reports", parent: "app/accounting" },
  "app/costcenters": { label: "Cost Centers", parent: "app/accounting" },
  "app/profit-centers": { label: "Profit Centers", parent: "app/accounting" },
  "app/journal-entries/:id": { label: "Journal Entry Details", parent: "app/journals" },
  "app/journals/:id": { label: "Journal Details", parent: "app/journals" },
  "app/credit-debit-notes": { label: "Credit / Debit Notes", parent: "app/accounting" },
  "app/credit-debit-notes/new": { label: "New Credit / Debit Note", parent: "app/credit-debit-notes" },
  "app/credit-debit-notes/:id/edit": { label: "Edit Credit / Debit Note", parent: "app/credit-debit-notes" },
  "app/credit-debit-notes/:id": { label: "Credit / Debit Note Details", parent: "app/credit-debit-notes" },

  // Customer subsections
  "app/customer-list": { label: "Customer List", parent: "app/customers" },
  "app/invoices": { label: "Invoices", parent: "app/accounting" },
  "app/invoices/:pageNumber": { label: "Invoices", parent: "app/accounting" },
  "app/invoices/client/:clientId": { label: "Client Invoices", parent: "app/invoices" },
  "app/invoices/byRental/:rentalId": { label: "Rental Invoices", parent: "app/invoices" },
  "app/add/invoice": { label: "Add Invoice", parent: "app/invoices" },
  "app/edit/invoice/:id": { label: "Edit Invoice", parent: "app/invoices" },
  "app/duplicate/invoice/:id": { label: "Duplicate Invoice", parent: "app/invoices" },
  "app/invoice/:id": { label: "Invoice Details", parent: "app/invoices" },
  "app/payments": { label: "Payments", parent: "app/accounting" },
  "app/payments/:pageNumber": { label: "Payments", parent: "app/accounting" },
  "app/payments/client/:clientId": { label: "Client Payments", parent: "app/payments" },
  "app/payment/:id": { label: "Payment Receipt", parent: "app/payments" },

  // Inventory subsections
  "app/inventory/home": { label: "Inventory", parent: "app/main" },
  "app/inventory/products": { label: "Products", parent: "app/inventory/home" },
  "app/inventory/products/add": { label: "Add Product", parent: "app/inventory/products" },
  "app/inventory/products/edit": { label: "Edit Product", parent: "app/inventory/products" },
  "app/inventory/products/categories": { label: "Categories", parent: "app/inventory/products" },
  "app/inventory/products/categories/add": { label: "Add Category", parent: "app/inventory/products/categories" },
  "app/inventory/items": { label: "Items", parent: "app/inventory/home" },
  "app/inventory/items/add": { label: "Add Item", parent: "app/inventory/items" },
  "app/inventory/items/edit/:id": { label: "Edit Item", parent: "app/inventory/items" },
  "app/inventory/:id": { label: "Inventory Details", parent: "app/inventory/home" },
  "app/inventory/warehouses": { label: "Warehouses", parent: "app/inventory/home" },
  "app/inventory/warehouse": { label: "Warehouse", parent: "app/inventory/home" },
  "app/inventory/unit-of-measure": { label: "Unit of Measure", parent: "app/inventory/home" },
  "app/inventory/units": { label: "Units", parent: "app/inventory/home" },
  "app/inventory/adjustments": { label: "Inventory Adjustments", parent: "app/inventory/home" },
  "app/inventory/adjustments/add": { label: "Add Adjustment", parent: "app/inventory/adjustments" },
  "app/inventory/adjustments/edit/:id": { label: "Edit Adjustment", parent: "app/inventory/adjustments" },
  "app/inventory/transactions": { label: "Inventory Transactions", parent: "app/inventory/home" },
  "app/inventory/transfers": { label: "Inventory Transfers", parent: "app/inventory/home" },
  "app/inventory/transfer/new": { label: "New Transfer", parent: "app/inventory/transfers" },
  "app/inventory/alerts": { label: "Inventory Alerts", parent: "app/inventory/home" },
  "app/inventory/reports": { label: "Inventory Reports", parent: "app/inventory/home" },
  "app/inventory/search": { label: "Inventory Search", parent: "app/inventory/home" },
  "app/inventory/settings": { label: "Inventory Settings", parent: "app/inventory/home" },
  "app/inventory-orders": { label: "Inventory Orders", parent: "app/inventory/home" },

  // Manufacturing subsections
  "app/manufacturing": { label: "manufacturing", namespace: "manufacturing", parent: "app/main" },
  "app/manufacturing/settings": { label: "manufacturing_settings", namespace: "manufacturing", parent: "app/manufacturing" },
  "app/manufacturing/boms": { label: "manufacturing_boms", namespace: "manufacturing", parent: "app/manufacturing" },
  "app/manufacturing/boms/:id": { label: "bom_details", namespace: "manufacturing", parent: "app/manufacturing/boms" },
  "app/manufacturing/work-centers": { label: "manufacturing_work_centers", namespace: "manufacturing", parent: "app/manufacturing" },
  "app/manufacturing/work-centers/:id": { label: "work_center_details", namespace: "manufacturing", parent: "app/manufacturing/work-centers" },
  "app/manufacturing/routings": { label: "manufacturing_routings", namespace: "manufacturing", parent: "app/manufacturing" },
  "app/manufacturing/routings/:id": { label: "routing_details", namespace: "manufacturing", parent: "app/manufacturing/routings" },
  "app/manufacturing/plans": { label: "manufacturing_planning", namespace: "manufacturing", parent: "app/manufacturing" },
  "app/manufacturing/plans/:id": { label: "production_plan_details", namespace: "manufacturing", parent: "app/manufacturing/plans" },
  "app/manufacturing/work-orders": { label: "manufacturing_work_orders", namespace: "manufacturing", parent: "app/manufacturing" },
  "app/manufacturing/work-orders/:id": { label: "work_order_details", namespace: "manufacturing", parent: "app/manufacturing/work-orders" },
  "app/manufacturing/quality": { label: "manufacturing_quality", namespace: "manufacturing", parent: "app/manufacturing" },
  "app/manufacturing/quality/capas/:id": { label: "capa_details", namespace: "manufacturing", parent: "app/manufacturing/quality" },
  "app/manufacturing/subcontracting": { label: "manufacturing_subcontracting", namespace: "manufacturing", parent: "app/manufacturing" },
  "app/manufacturing/subcontracting/:id": { label: "subcontract_details", namespace: "manufacturing", parent: "app/manufacturing/subcontracting" },
  "app/manufacturing/maintenance": { label: "manufacturing_maintenance", namespace: "manufacturing", parent: "app/manufacturing" },
  "app/manufacturing/maintenance/:id": { label: "maintenance_request_details", namespace: "manufacturing", parent: "app/manufacturing/maintenance" },
  "app/manufacturing/reports": { label: "manufacturing_reports", namespace: "manufacturing", parent: "app/manufacturing" },

  // User Management
  "app/users": { label: "Users", parent: "app/settings" },
  "app/groups": { label: "User Groups", parent: "app/settings" },
  "app/add/group": { label: "Add Group", parent: "app/groups" },
  "app/edit/group/:id": { label: "Edit Group", parent: "app/groups" },

  // Properties subsections
  "app/properties/add": { label: "Add Property", parent: "app/properties" },
  "app/properties/edit/:id": { label: "Edit Property", parent: "app/properties" },
  "app/properties/view/:id": { label: "Property Details", parent: "app/properties" },

  // Rentals subsections
  "app/rentals/home": { label: "Rentals Home", parent: "app/rentals" },
  "app/rentals/all": { label: "All Rentals", parent: "app/rentals/home" },
  "app/rentals/unpaid": { label: "Unpaid Rentals", parent: "app/rentals/home" },
  "app/rentals/add": { label: "Add Rental", parent: "app/rentals/home" },
  "app/rentals/edit/:id": { label: "Edit Rental", parent: "app/rentals/home" },

  // Rental Document Templates Routes
  "app/rentals/doc/:type": { label: "Rental Documents", parent: "app/rentals/home" },
  "app/rentals/doc/:type/add": { label: "Add Document", parent: "app/rentals/doc/:type" },
  "app/rentals/doc/:type/edit/:id": { label: "Edit Document", parent: "app/rentals/doc/:type" },
  "app/rentals/doc/:type/view/:id": { label: "View Document", parent: "app/rentals/doc/:type" },

  // Buildings subsections
  "app/buildings/:id/details": { label: "Building Details", parent: "app/buildings" },

  // Purchases subsections
  "app/purchases/add": { label: "Add Purchase", parent: "app/purchases" },
  "app/purchases/edit/:id": { label: "Edit Purchase", parent: "app/purchases" },
  "app/purchases/duplicate/:id": { label: "Duplicate Purchase", parent: "app/purchases" },
  "app/purchases/view/:id": { label: "Purchase Details", parent: "app/purchases" },

  // Payroll subsections
  "app/payrolls/generate": { label: "Generate Payroll", parent: "app/payrolls" },
  "app/payrolls/bulk-generate": { label: "Bulk Generate Payroll", parent: "app/payrolls" },
  "app/payrolls/list": { label: "Payroll List", parent: "app/payrolls" },
  "app/payrolls/analytics": { label: "Payroll Analytics", parent: "app/payrolls" },
  "app/payrolls/payments": { label: "Payroll Payments", parent: "app/payrolls" },

  // Assets subsections
  "app/assets/add": { label: "Add Asset", parent: "app/assets" },
  "app/assets/edit/:id": { label: "Edit Asset", parent: "app/assets" },
  "app/assets/view/:id": { label: "Asset Details", parent: "app/assets" },

  // Attendance subsections
  "app/hr": { label: "Human Resources", parent: "app/main" },
  "app/hr/employees": { label: "Employees", parent: "app/hr" },
  "app/attendance/home": { label: "Attendance Home", parent: "app/main" },
  "app/attendance/reports": { label: "Attendance Reports", parent: "app/attendance/home" },
  "app/attendance/records": { label: "Attendance Records", parent: "app/attendance/home" },
  "app/attendance/checkin": { label: "Check In/Out", parent: "app/attendance/home" },
  "app/attendance/shifts": { label: "Shift Management", parent: "app/attendance/home" },
  "app/attendance/holidays": { label: "Holiday Management", parent: "app/attendance/home" },
  "app/attendance/employees": { label: "Employee Management", parent: "app/attendance/home" },
  "app/attendance/timetracking": { label: "Time Tracking", parent: "app/attendance/home" },
  "app/attendances/record": { label: "Record Attendance", parent: "app/attendances" },

  // Clinic Management subsections
  "app/clinic/doctors": { label: "Doctors", parent: "app/clinic" },
  "app/clinic/doctors/add": { label: "Add Doctor", parent: "app/clinic/doctors" },
  "app/clinic/doctors/edit/:id": { label: "Edit Doctor", parent: "app/clinic/doctors" },
  "app/clinic/doctors/:id": { label: "Doctor Details", parent: "app/clinic/doctors" },
  "app/clinic/patients": { label: "Patients", parent: "app/clinic" },
  "app/clinic/patients/add": { label: "Add Patient", parent: "app/clinic/patients" },
  "app/clinic/patients/edit/:id": { label: "Edit Patient", parent: "app/clinic/patients" },
  "app/clinic/patients/:id": { label: "Patient Details", parent: "app/clinic/patients" },
  "app/clinic/appointments": { label: "Appointments", parent: "app/clinic" },
  "app/clinic/appointments/add": { label: "Add Appointment", parent: "app/clinic/appointments" },
  "app/clinic/appointments/edit": { label: "Edit Appointment", parent: "app/clinic/appointments" },
  "app/clinic/appointments/edit/:id": { label: "Edit Appointment", parent: "app/clinic/appointments" },
  "app/clinic/appointments/view": { label: "View Appointment", parent: "app/clinic/appointments" },
  "app/clinic/appointments/view/:id": { label: "Appointment Details", parent: "app/clinic/appointments" },
  "app/clinic/appointments/:id": { label: "Appointment Details", parent: "app/clinic/appointments" },
  "app/clinic/lab-results": { label: "Lab Results", parent: "app/clinic" },
  "app/clinic/lab-results/add": { label: "Add Lab Result", parent: "app/clinic/lab-results" },
  "app/clinic/lab-results/edit/:id": { label: "Edit Lab Result", parent: "app/clinic/lab-results" },
  "app/clinic/lab-results/:id": { label: "Lab Result Details", parent: "app/clinic/lab-results" },
  "app/clinic/lab-tests": { label: "Lab Tests", parent: "app/clinic" },
  "app/clinic/lab-tests/add": { label: "Add Lab Test", parent: "app/clinic/lab-tests" },
  "app/clinic/lab-tests/edit/:id": { label: "Edit Lab Test", parent: "app/clinic/lab-tests" },
  "app/clinic/lab-tests/:id": { label: "Lab Test Details", parent: "app/clinic/lab-tests" },
  "app/clinic/medical-records": { label: "Medical Records", parent: "app/clinic" },
  "app/clinic/medical-records/add": { label: "Add Medical Record", parent: "app/clinic/medical-records" },
  "app/clinic/medical-records/edit/:id": { label: "Edit Medical Record", parent: "app/clinic/medical-records" },
  "app/clinic/medical-records/:id": { label: "Medical Record Details", parent: "app/clinic/medical-records" },
  "app/clinic/prescriptions": { label: "Prescriptions", parent: "app/clinic" },
  "app/clinic/prescriptions/add": { label: "Add Prescription", parent: "app/clinic/prescriptions" },
  "app/clinic/prescriptions/edit/:id": { label: "Edit Prescription", parent: "app/clinic/prescriptions" },
  "app/clinic/prescriptions/:id": { label: "Prescription Details", parent: "app/clinic/prescriptions" },
  "app/clinic/medications": { label: "Medications", parent: "app/clinic" },
  "app/clinic/medications/add": { label: "Add Medication", parent: "app/clinic/medications" },
  "app/clinic/medications/edit/:id": { label: "Edit Medication", parent: "app/clinic/medications" },
  "app/clinic/medications/:id": { label: "Medication Details", parent: "app/clinic/medications" },

  // Letters subsections
  "app/letters/home": { label: "Letters Home", parent: "app/letters" },
  "app/letters/add": { label: "Add Letter", parent: "app/letters" },
  "app/letters/create": { label: "Create Letter", parent: "app/letters" },
  "app/letters/edit/:id": { label: "Edit Letter", parent: "app/letters" },
  "app/letters/view/:id": { label: "Letter Details", parent: "app/letters" },
  "app/letters/templates": { label: "Templates", parent: "app/letters" },
  "app/letters/templates/library": { label: "Template Library", parent: "app/letters/templates" },
  "app/letters/approvals": { label: "Approvals", parent: "app/letters" },
  "app/letters/approvals/queue": { label: "Approval Queue", parent: "app/letters/approvals" },
  "app/letters/inbox": { label: "Letter Inbox", parent: "app/letters" },
  "app/letters/outbox": { label: "Letter Outbox", parent: "app/letters" },
  "app/letters/search": { label: "Search", parent: "app/letters" },
  "app/letters/drafts": { label: "Drafts", parent: "app/letters" },
  "app/letters/sent": { label: "Sent", parent: "app/letters" },

  // Coupons subsections
  "app/coupons/print": { label: "Print Coupon", parent: "app/coupons" },
  "app/coupons/print/:id": { label: "Print Coupon", parent: "app/coupons" },

  // Barcode and Labels
  "app/label/print": { label: "Print Label", parent: "app/barcode-settings" },
  "app/label/print/:id": { label: "Print Label", parent: "app/barcode-settings" },
  "app/qrcode": { label: "QR Code Generator", parent: "app/main" },
  "app/qrcode/create": { label: "Create QR Code", parent: "app/qrcode" },
  "app/qrcode/edit/:id": { label: "Edit QR Code", parent: "app/qrcode" },

  // Law Firm Management Routes
  "app/law-firm": { label: "Law Firm Management", parent: "app/main" },
  "app/law-firm/dashboard": { label: "Law Firm Dashboard", parent: "app/law-firm" },

  // Case Management Routes
  "app/law-firm/cases": { label: "Cases", parent: "app/law-firm" },
  "app/law-firm/cases/new": { label: "New Case", parent: "app/law-firm/cases" },
  "app/law-firm/cases/:id": { label: "Case Details", parent: "app/law-firm/cases" },
  "app/law-firm/cases/:id/edit": { label: "Edit Case", parent: "app/law-firm/cases" },
  "app/law-firm/cases/:caseId/time-entries": { label: "Case Time Entries", parent: "app/law-firm/cases/:caseId" },
  "app/law-firm/cases/:caseId/tasks": { label: "Case Tasks", parent: "app/law-firm/cases/:caseId" },
  "app/law-firm/cases/:caseId/expenses": { label: "Case Expenses", parent: "app/law-firm/cases/:caseId" },

  // Time Entry Routes
  "app/law-firm/time-entries": { label: "Time Entries", parent: "app/law-firm" },
  "app/law-firm/time-entries/new": { label: "New Time Entry", parent: "app/law-firm/time-entries" },
  "app/law-firm/time-entries/:entryId/edit": { label: "Edit Time Entry", parent: "app/law-firm/time-entries" },

  // Task Routes
  "app/law-firm/tasks": { label: "Tasks", parent: "app/law-firm" },
  "app/law-firm/tasks/new": { label: "New Task", parent: "app/law-firm/tasks" },
  "app/law-firm/tasks/:taskId/edit": { label: "Edit Task", parent: "app/law-firm/tasks" },

  // Billing Routes
  "app/law-firm/billing": { label: "Billing", parent: "app/law-firm" },

  // Expense Routes
  "app/law-firm/expenses": { label: "Expenses", parent: "app/law-firm" },
  "app/law-firm/expenses/new": { label: "New Expense", parent: "app/law-firm/expenses" },
  "app/law-firm/expenses/:expenseId/edit": { label: "Edit Expense", parent: "app/law-firm/expenses" },

  // Report Routes
  "app/law-firm/reports": { label: "Reports", parent: "app/law-firm" },
  "app/law-firm/reports/financial": { label: "Financial Reports", parent: "app/law-firm/reports" },
  "app/law-firm/reports/time-utilization": { label: "Time Utilization", parent: "app/law-firm/reports" },
  "app/law-firm/reports/attorney-performance": { label: "Attorney Performance", parent: "app/law-firm/reports" },
  "app/law-firm/reports/client-billing": { label: "Client Billing", parent: "app/law-firm/reports" },
  "app/law-firm/reports/case-profitability": { label: "Case Profitability", parent: "app/law-firm/reports" },
  "app/law-firm/reports/practice-areas": { label: "Practice Areas", parent: "app/law-firm/reports" },
  "app/law-firm/reports/realization": { label: "Realization Rate", parent: "app/law-firm/reports" },
  "app/law-firm/reports/custom": { label: "Custom Reports", parent: "app/law-firm/reports" },

  // Client Management Routes
  "app/law-firm/clients": { label: "Clients", parent: "app/law-firm" },

  // Trust Account Routes
  "app/law-firm/trust-accounts": { label: "Trust Accounts", parent: "app/law-firm" },

  // Document Management Routes
  "app/law-firm/documents": { label: "Documents", parent: "app/law-firm" },

  // Court Calendar Routes
  "app/law-firm/calendar": { label: "Court Calendar", parent: "app/law-firm" },

  // Conflict Checker Routes
  "app/law-firm/conflicts": { label: "Conflict Checker", parent: "app/law-firm" },

  // Client Portal Routes
  "app/law-firm/client-portal": { label: "Client Portal", parent: "app/law-firm" },

  // Matter Management Routes
  "app/law-firm/matters": { label: "Matters", parent: "app/law-firm" },

  // Construction Management Routes
  "app/construction": { label: "Construction Management", parent: "app/main" },
  "app/construction/projects": { label: "Projects", parent: "app/construction" },
  "app/construction/projects/add": { label: "Add Project", parent: "app/construction/projects" },
  "app/construction/projects/:projectId": { label: "Project Dashboard", parent: "app/construction/projects" },
  "app/construction/projects/:projectId/edit": { label: "Edit Project", parent: "app/construction/projects" },

  // BOQ Routes
  "app/construction/projects/:projectId/boq": { label: "Bill of Quantities (BOQ)", parent: "app/construction/projects/:projectId" },
  "app/construction/projects/:projectId/boq/add": { label: "Add BOQ Item", parent: "app/construction/projects/:projectId/boq" },
  "app/construction/projects/:projectId/boq/:id": { label: "BOQ Details", parent: "app/construction/projects/:projectId/boq" },
  "app/construction/projects/:projectId/boq/:id/edit": { label: "Edit BOQ Item", parent: "app/construction/projects/:projectId/boq" },

  // Progress Billing Routes
  "app/construction/projects/:projectId/progress-billing": { label: "Progress Billing", parent: "app/construction/projects/:projectId" },
  "app/construction/projects/:projectId/progress-billing/add": { label: "Add Progress Bill", parent: "app/construction/projects/:projectId/progress-billing" },
  "app/construction/projects/:projectId/progress-billing/:id": { label: "Progress Bill Details", parent: "app/construction/projects/:projectId/progress-billing" },
  "app/construction/projects/:projectId/progress-billing/:id/edit": { label: "Edit Progress Bill", parent: "app/construction/projects/:projectId/progress-billing" },

  // Sites Routes
  "app/construction/projects/:projectId/sites": { label: "Sites", parent: "app/construction/projects/:projectId" },

  // Daily Reports Routes
  "app/construction/projects/:projectId/daily-reports": { label: "Daily Reports", parent: "app/construction/projects/:projectId" },

  // Subcontractors Routes
  "app/construction/projects/:projectId/subcontractors": { label: "Subcontractors", parent: "app/construction/projects/:projectId" },

  // Material Requests Routes
  "app/construction/projects/:projectId/material-requests": { label: "Material Requests", parent: "app/construction/projects/:projectId" },

  // Budget Routes
  "app/construction/projects/:projectId/budget": { label: "Budget", parent: "app/construction/projects/:projectId" },

  // Expenses Routes
  "app/construction/projects/:projectId/expenses": { label: "Expenses", parent: "app/construction/projects/:projectId" },

  // Reports Routes
  "app/construction/projects/:projectId/reports": { label: "Reports", parent: "app/construction/projects/:projectId" },
  "app/construction/projects/:projectId/reports/financial": { label: "Financial Report", parent: "app/construction/projects/:projectId/reports" },
  "app/construction/projects/:projectId/reports/progress": { label: "Progress Report", parent: "app/construction/projects/:projectId/reports" },
  "app/construction/projects/:projectId/reports/schedule": { label: "Schedule Report", parent: "app/construction/projects/:projectId/reports" },
  "app/construction/projects/:projectId/reports/resource": { label: "Resource Report", parent: "app/construction/projects/:projectId/reports" },
  "app/construction/projects/:projectId/reports/material": { label: "Material Report", parent: "app/construction/projects/:projectId/reports" },
  "app/construction/projects/:projectId/reports/analytics": { label: "Analytics", parent: "app/construction/projects/:projectId/reports" },

  // Add more routes as needed
};

/**
 * Match actual path against route pattern with parameters
 * Example: matchRoute("app/construction/projects/123", "app/construction/projects/:projectId")
 * Returns: { matched: true, params: { projectId: "123" } }
 */
const matchRoute = (actualPath, routePattern) => {
  const actualSegments = actualPath.split("/");
  const patternSegments = routePattern.split("/");

  if (actualSegments.length !== patternSegments.length) {
    return { matched: false, params: {} };
  }

  const params = {};
  for (let i = 0; i < patternSegments.length; i++) {
    if (patternSegments[i].startsWith(":")) {
      // This is a parameter, capture the actual value
      const paramName = patternSegments[i].substring(1);
      params[paramName] = actualSegments[i];
    } else if (patternSegments[i].toLowerCase() !== actualSegments[i].toLowerCase()) {
      // Non-parameter segments must match exactly
      return { matched: false, params: {} };
    }
  }

  return { matched: true, params };
};

/**
 * Find matching route config for an actual path
 */
const findMatchingRoute = (actualPath) => {
  // First try exact match
  if (routeConfig[actualPath]) {
    return { config: routeConfig[actualPath], params: {}, pattern: actualPath };
  }

  const caseInsensitiveExact = Object.keys(routeConfig).find(
    (pattern) => !pattern.includes(":") && pattern.toLowerCase() === actualPath.toLowerCase()
  );
  if (caseInsensitiveExact) {
    return { config: routeConfig[caseInsensitiveExact], params: {}, pattern: caseInsensitiveExact };
  }

  // Try matching against parametrized routes
  const matches = Object.entries(routeConfig)
    .filter(([pattern]) => pattern.includes(":"))
    .map(([pattern, config]) => {
      const match = matchRoute(actualPath, pattern);
      return match.matched ? { config, params: match.params, pattern } : null;
    })
    .filter(Boolean)
    .sort((a, b) => {
      const aStatic = a.pattern.split("/").filter((segment) => !segment.startsWith(":")).length;
      const bStatic = b.pattern.split("/").filter((segment) => !segment.startsWith(":")).length;
      return b.pattern.length - a.pattern.length || bStatic - aStatic;
    });

  if (matches.length) return matches[0];

  return null;
};

/**
 * Replace parameters in a path pattern with actual values
 */
const buildActualPath = (pattern, params) => {
  let actualPath = pattern;
  for (const [key, value] of Object.entries(params)) {
    actualPath = actualPath.replace(`:${key}`, value);
  }
  return actualPath;
};

const prettifySegment = (value) =>
  String(value || "")
    .split("?")[0]
    .replace(/[-_]/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());

/**
 * Generate breadcrumb links based on the current path
 *
 * @param {string} currentPath - Current path (without language prefix)
 * @param {string} currentLang - Current language code
 * @param {Object} customLabels - Optional custom labels for specific path segments
 * @param {Object} customParams - Optional parameters to include in URLs (e.g., IDs)
 * @returns {Array} Array of breadcrumb link objects
 */
export const generateBreadcrumbs = (
  currentPath,
  currentLang,
  customLabels = {},
  customParams = {},
  translateLabel = null
) => {
  // Remove leading and trailing slashes and language prefix if present
  const normalizedPath = currentPath
    .replace(/^\/?|\/?$/g, "")
    .replace(new RegExp(`^${currentLang}/`), "");

  if (!normalizedPath || normalizedPath === "app/main") return [];

  const pathSegments = normalizedPath.split("/").filter(Boolean);
  const breadcrumbs = [];
  const processedPaths = new Set();
  const capturedParams = {}; // Store all captured parameters

  const processPath = (actualPath, inheritedParams = {}) => {
    // Avoid processing the same path twice (prevents infinite loops)
    if (processedPaths.has(actualPath)) return;
    processedPaths.add(actualPath);

    // Find matching route (exact or parametrized)
    const match = findMatchingRoute(actualPath);
    if (!match) return;

    const { config, params, pattern } = match;

    // Merge captured parameters
    Object.assign(capturedParams, params);
    Object.assign(capturedParams, inheritedParams);

    // Process parent path first (for correct order)
    if (config.parent) {
      // Build actual parent path with captured parameters
      const actualParentPath = buildActualPath(config.parent, capturedParams);
      processPath(actualParentPath, capturedParams);
    }

    if (!config.hide) {
      const customLabel = customLabels[actualPath] || customLabels[pattern];
      const label = customLabel || config.label;
      breadcrumbs.push({
        label,
        href: `/${currentLang}/${actualPath}`,
        namespace: config.namespace,
      });
    }
  };

  const fullPathMatch = findMatchingRoute(normalizedPath);
  if (fullPathMatch) {
    processPath(normalizedPath, customParams);
    return breadcrumbs;
  }

  // Fallback for routes not yet listed in routeConfig: use the nearest known
  // parent and a clean label for the current page instead of leaking raw IDs.
  for (let i = pathSegments.length - 1; i > 0; i--) {
    const parentSegment = pathSegments.slice(0, i).join("/");
    const parentMatch = findMatchingRoute(parentSegment);
    if (!parentMatch) continue;

    processPath(parentSegment, customParams);
    const lastSegment = pathSegments[pathSegments.length - 1];
    const label = customLabels[normalizedPath] || customLabels[lastSegment] || prettifySegment(lastSegment);
    if (label && !/^[a-f0-9]{16,}$/i.test(label)) {
      breadcrumbs.push({ label, href: null });
    }
    return breadcrumbs;
  }

  return breadcrumbs;
};

/**
 * Get the current path from location object
 *
 * @param {Object} location - React Router location object
 * @returns {string} Current path without query parameters
 */
export const getCurrentPath = (location) => {
  return location.pathname;
};

const breadcrumbUtils = {
  generateBreadcrumbs,
  getCurrentPath,
};

export default breadcrumbUtils;
